/**
 * Delivery auto-approval PIN — big mono 4-digit display, regenerate,
 * enable/disable toggle. Wired: GET/POST /api/services/delivery-pin
 * (optimistic toggle with revert). Port of screens-services.jsx DeliveryPin.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { fonts, radius, space } from '@bero/tokens';
import { bool, str } from '@/components/gate/api';
import { InfoNote, Toggle } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Screen, Skeleton, TopBar } from '@/ui';

export default function DeliveryPinScreen() {
  const { colors } = useTheme();
  const { t } = useT();

  const [enabled, setEnabled] = React.useState(true);
  const [pin, setPin] = React.useState<string | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [regenerating, setRegenerating] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<unknown>('/api/services/delivery-pin');
        if (cancelled) return;
        const rec = (body && typeof body === 'object' && 'deliveryPin' in body)
          ? (body as { deliveryPin: unknown }).deliveryPin : undefined;
        setPin((str(rec, 'pin', 'code') ?? str(body, 'pin', 'code') ?? '').slice(0, 4) || null);
        setEnabled(bool(rec, 'enabled') ?? bool(body, 'enabled', 'autoApprove') ?? true);
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (next: boolean) => {
    setEnabled(next); // optimistic
    setNotice(null);
    try {
      await api.post<unknown>('/api/services/delivery-pin', { enabled: next });
    } catch {
      setEnabled(!next); // revert
      setNotice(t('np_update_error'));
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    setNotice(null);
    try {
      const body = await api.post<unknown>('/api/services/delivery-pin', { regenerate: true });
      const rec = (body && typeof body === 'object' && 'deliveryPin' in body)
        ? (body as { deliveryPin: unknown }).deliveryPin : undefined;
      const next = (str(rec, 'pin', 'code') ?? str(body, 'pin', 'code') ?? '').slice(0, 4);
      if (next) setPin(next);
      setNotice(t('dp_regenerated'));
    } catch {
      setNotice(t('dp_regen_error'));
    } finally {
      setRegenerating(false);
    }
  };

  const digits = (pin ?? '····').split('');

  return (
    <Screen scroll={false}>
      <TopBar title={t('dp_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s4 }}>
            <Skeleton h={88} />
            <Skeleton h={64} />
          </View>
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="package" size={40} color={colors.textTertiary} />}
            title={t('dp_err_title')}
            body={t('dp_err_body')}
          />
        ) : (
          <>
            <View style={{
              backgroundColor: colors.canvas1, borderRadius: radius.lg, padding: space.s4,
              flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <View style={{ flex: 1, paddingRight: space.s3 }}>
                <AppText variant="bodyMd" weight="500">{t('dp_auto_approve')}</AppText>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                  {t('dp_auto_approve_sub')}
                </AppText>
              </View>
              <Toggle on={enabled} onChange={toggle} label={t('dp_auto_approve')} />
            </View>

            {enabled ? (
              <>
                {/* the one amber accent on this screen: the PIN itself */}
                <View style={{
                  marginTop: space.s6, borderWidth: 1, borderColor: colors.accent500,
                  borderRadius: radius.lg, backgroundColor: colors.accent100, padding: space.s4,
                }}>
                  <AppText variant="label" style={{ color: colors.accent700 }}>{t('parcels_pin_title')}</AppText>
                  <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: space.s3, marginTop: 6 }}>
                    <AppText mono weight="500" style={{ fontSize: 34, lineHeight: 42, letterSpacing: 6, fontFamily: fonts.monoMedium, color: colors.textPrimary }}>
                      {digits.join(' ')}
                    </AppText>
                    <AppText variant="bodySm" tone="secondary">{t('dp_rotates')}</AppText>
                  </View>
                  <AppText variant="bodySm" tone="secondary" style={{ marginTop: 6 }}>
                    {t('parcels_pin_sub')}
                  </AppText>
                </View>
                <Button
                  title={regenerating ? t('dp_generating') : t('dp_regenerate')}
                  kind="secondary"
                  loading={regenerating}
                  style={{ marginTop: space.s3 }}
                  onPress={regenerate}
                />
                <InfoNote icon="shield" tint={colors.primary500} style={{ marginTop: space.s4 }}>
                  {t('dp_warn')}
                </InfoNote>
              </>
            ) : null}

            {notice ? (
              <InfoNote icon="info" style={{ marginTop: space.s4 }}>{notice}</InfoNote>
            ) : null}
          </>
        )}
      </Screen>
    </Screen>
  );
}
